import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { accessTokenSuccess, logout } from '../authSlice';

const isEnvelope = (body) =>
  body && typeof body === 'object' && !Array.isArray(body) && 'data' in body;

const isErrorEnvelope = (body) =>
  body &&
  typeof body === 'object' &&
  body.error &&
  typeof body.error === 'object' &&
  'code' in body.error;

const unwrapSuccess = (response) => {
  if (!response || !response.data) return response;
  const body = response.data;
  if (isEnvelope(body)) {
    return {
      ...response,
      data: body.data,
      meta: { ...(response.meta || {}), envelope: body.meta },
    };
  }
  return response;
};

const normalizeError = (response) => {
  if (!response || !response.error) return response;
  const err = response.error;
  const status = err.status;
  const raw = err.data;

  let normalized;
  if (isErrorEnvelope(raw)) {
    normalized = {
      code: raw.error.code,
      message: raw.error.message,
      details: raw.error.details,
      status,
    };
  } else if (raw && typeof raw === 'object') {
    // Fallback for endpoints that didn't go through the envelope handler
    normalized = {
      code: raw.code || (status === 401 ? 'not_authenticated' : 'internal_error'),
      message:
        raw.message ||
        raw.detail ||
        (typeof raw.error === 'string' ? raw.error : null) ||
        'Unbekannter Fehler',
      details: raw.details || (typeof raw === 'object' ? raw : undefined),
      status,
    };
  } else {
    normalized = {
      code: status === 'FETCH_ERROR' ? 'network_error' : 'internal_error',
      message:
        status === 'FETCH_ERROR'
          ? 'Verbindung zum Server fehlgeschlagen.'
          : 'Unbekannter Fehler',
      details: undefined,
      status,
    };
  }

  return {
    ...response,
    error: { ...err, status, data: normalized },
  };
};

const customFetchBaseQuery = (baseUrl, prepareHeaders) => {
  const baseQuery = fetchBaseQuery({ baseUrl, prepareHeaders });

  return async (endpoint, body, extraOptions) => {
    const isTokenRefresh =
      endpoint?.url?.indexOf('/auth/token/refresh/') !== -1;

    let response = await baseQuery(endpoint, body, extraOptions);

    if (response.error && response.error.status === 401) {
      if (
        !isTokenRefresh &&
        window.location.href?.indexOf('/login/') === -1 &&
        body.getState().auth.stayLoggedIn
      ) {
        const refreshResponse = await baseQuery(
          {
            url: `/auth/token/refresh/`,
            method: 'POST',
            body: {
              refresh: body.getState().auth.refreshToken,
            },
          },
          body,
          extraOptions
        );
        if (refreshResponse.data) {
          // Refresh response is enveloped: { data: { access, access_expiration } }
          const payload = isEnvelope(refreshResponse.data)
            ? refreshResponse.data.data
            : refreshResponse.data;
          body.dispatch(
            accessTokenSuccess({
              accessToken: payload.access,
              accessExpiresAt: payload.access_expiration,
            })
          );
          response = await baseQuery(endpoint, body, extraOptions);
          if (response.data) response = unwrapSuccess(response);
          if (response.error) response = normalizeError(response);
          return response;
        } else {
          body.dispatch(logout());
        }
      } else {
        body.dispatch(logout());
      }
    }

    if (response.data) response = unwrapSuccess(response);
    if (response.error) response = normalizeError(response);
    return response;
  };
};

export const baseQuery = customFetchBaseQuery(
  process.env.REACT_APP_API_URL,
  (headers, { getState, dispatch }) => {
    const token = getState().auth?.accessToken || null;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      return headers;
    }
  }
);

/**
 * Normalize any error caught from RTK Query .unwrap() or returned by a hook
 * into { code, message, details, status }.
 */
export const getApiError = (err) => {
  if (!err) {
    return { code: 'internal_error', message: 'Unbekannter Fehler' };
  }
  // RTK Query attaches the FetchBaseQueryError on .error or directly when thrown via .unwrap()
  const candidate = err.data && err.data.code ? err.data : err;
  if (candidate.code && candidate.message) {
    return {
      code: candidate.code,
      message: candidate.message,
      details: candidate.details,
      status: candidate.status,
    };
  }
  if (err.error && typeof err.error === 'object') {
    return getApiError(err.error);
  }
  return {
    code: 'internal_error',
    message:
      err.message || (typeof err === 'string' ? err : 'Unbekannter Fehler'),
    details: undefined,
    status: err.status,
  };
};
